import { useParams } from "react-router-dom"
import { AssetClass } from "@hydrologiq/gql-experience/graphql"
import { useAsset } from "../hooks/useAsset"
import Usage from "../components/usage/Usage"
import Informative from "@hydrologiq/shared/Informative"
import { COPY_KEYS, DEFAULT_COPY_MAPPINGS } from "@hydrologiq/shared/copy/constants"
import { useCopyValues } from "@hydrologiq/shared/providers/helpers/useCopy"
import HybridDownload from "@hydrologiq/shared/reporting/HybridDownload"
import { getAssignmentEndDate, getAssignmentStartDate, getMonitoredAssignments } from "../utils/assignmentDates"

/** Minimal shape for reading a child asset's class off the fetched asset. */
interface AssetClassMember {
  class?: AssetClass | null
}

/** Minimal shape describing the electrolyser-membership inputs on an asset. */
interface ElectrolyserMembership {
  class?: AssetClass | null
  children?: AssetClassMember[] | null
}

/**
 * Derive whether an asset is electrolyser-capable from its class / hybrid
 * membership: true when the asset itself is an electrolyser, or when it is a
 * hybrid whose children include an electrolyser. Drives the shared download's
 * hydrogen-weight column pruning.
 */
function deriveHasElectrolyser(asset: ElectrolyserMembership | undefined): boolean {
  if (!asset) return false
  if (asset.class === AssetClass.Electrolyser) return true
  return (asset.children ?? []).some((child) => child?.class === AssetClass.Electrolyser)
}

const Asset = () => {
  const { assetId = "" } = useParams()
  const { asset, loading } = useAsset(assetId)
  const source = asset?.source ?? "timescale"
  const copy = useCopyValues({
    [COPY_KEYS.HEADINGS.ASSET]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.HEADINGS.ASSET],
  })

  // When source is "timescale" (default): render the shared HybridDownload
  // fetching from the REST endpoint `/v1/assets/{id}/reporting`.
  if (source === "timescale" && !loading && asset) {
    // Prefer `range` (self + children rolled up) so a parent's download date
    // range spans its modules' data connections, not just its own.
    const monitoredAssignments = getMonitoredAssignments(
      asset.assignments?.range ?? asset.assignments?.all ?? [],
      asset.assignments?.monitored
    )

    // Assignment-based date bounds stay in the assets section, derived here and
    // passed to the shared component as YYYY-MM-DD min/max bounds.
    const minDate = monitoredAssignments.length
      ? getAssignmentStartDate(monitoredAssignments)?.split("T")[0]
      : undefined
    const maxDate = monitoredAssignments.length
      ? getAssignmentEndDate(monitoredAssignments)?.split("T")[0]
      : undefined

    return (
      <HybridDownload
        scope="assets"
        id={asset.id}
        name={asset.name ?? ""}
        minDate={minDate}
        maxDate={maxDate}
        hasElectrolyser={deriveHasElectrolyser(asset)}
      />
    )
  }

  // When source is "influx": render existing Usage (unchanged behaviour)
  if (!loading && asset && !asset.assignments?.all?.length) {
    return (
      <Informative
        heading={`No monitoring data available for the ${copy[COPY_KEYS.HEADINGS.ASSET].toLocaleLowerCase()}`}
        text="Please activate monitoring on the overview page."
      />
    )
  }
  return <Usage asset={asset} />
}

export default Asset
